import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertImageNameToWeb } from "../../../helpers/helperFunctions";
import {
  ORDER_ITEM_ADDON_TYPE,
  ORDER_ITEM_DESCRIPTION_TYPE,
} from "../../../helpers/const";
import { Link } from "react-router-dom";
import { getTrackMakesAndModels } from "../../../store/slices/ProductFinder/ProductFinderSlice";
import Button from "../../atoms/Button";
import EmailPopup from "../../atoms/EmailPopup/EmailPopup";
import { getQuoteById } from "../../../store/slices/CustomerSlice/CustomerSlice";
import { sendToClient } from "../../../store/slices/OrderSlice/OrderSlice";

export default function QuoteDetail() {
  const { id: quote_num } = useParams();
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  const [loadedQuote, setLoadedQuote] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (quote_num) {
      dispatch(getQuoteById(quote_num)).then((response) => {
        if (response?.payload.quote) {
          setLoadedQuote(response.payload.quote);
        }
      });
    }
  }, [quote_num, dispatch]);

  const fetchTrackMatches = (productCode) => {
    dispatch(getTrackMakesAndModels({ product_code: productCode }));
  };

  const handleSendEmail = ({ emails }) => {
    // Send quote using the same method as orders but with isSubOrder = false
    console.log(emails);
    let final_emails = [];
    for (let email of emails) {
      final_emails.push({ email: email });
    }
    console.log(emails, final_emails);
    if (emails && emails.length > 0) {
      dispatch(
        sendToClient({
          recipients: final_emails,
          customer_order_num: quote_num,
          isSubOrder: false, // Set to false for quotes
        })
      );
    }
    setShowEmailPopup(false);
  };

  return (
    <>
      {loadedQuote && (
        <main className="relative lg:min-h-full bg-gradient-to-br">
          <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:grid lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-4 lg:py-16 xl:gap-x-24">
            <div className="lg:col-start-0">
              <h1 className="text-sm font-medium text-[var(--primary-color)]">
                Quote Number {loadedQuote.quote_num}
                {loadedQuote.customer_po && ` (PO ${loadedQuote.customer_po})`}
              </h1>

              <div className="mt-4 space-y-2">
                {loadedQuote.date_of_quote && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Quote Date:</span>
                    <span className="font-medium">
                      {new Date(loadedQuote.date_of_quote).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              <ul className="mt-6 divide-y divide-gray-200 border-gray-200 text-sm font-medium text-gray-500">
                {[...loadedQuote.quote_items]
                  .sort((a, b) => a.item.id - b.item.id)
                  .map((group) => {
                    // For description and addon items, only show the description
                    if (
                      [
                        ORDER_ITEM_DESCRIPTION_TYPE,
                        ORDER_ITEM_ADDON_TYPE,
                      ].includes(group.item.item_type)
                    ) {
                      return (
                        <li key={group.item.id} className="py-4">
                          <div className="text-gray-700">
                            ** <i>{group.item.description}</i>
                          </div>
                        </li>
                      );
                    }

                    return (
                      <li
                        key={group.item.id}
                        className="flex flex-col space-y-4 py-6"
                      >
                        <div className="flex space-x-6">
                          <img
                            alt={group.item.product_code}
                            src={
                              process.env.REACT_APP_IMAGE_URL +
                              convertImageNameToWeb(group.item.image_name)
                            }
                            className="h-32 w-32 flex-none rounded-md bg-gray-100 object-contain p-2"
                            onError={(e) => {
                              // Prevent infinite loop by only setting default once
                              if (!e.target.src.includes("default.jpg")) {
                                e.target.src =
                                  process.env.REACT_APP_IMAGE_URL +
                                  "default.jpg";
                              }
                            }}
                          />

                          <div className="flex-auto space-y-1">
                            <h3 className="text-gray-900">
                              <p>{group.item.description}</p>
                            </h3>
                            <p>
                              {group.item.item_price
                                ? `$${group.item.item_price?.toFixed(2)}`
                                : "-"}{" "}
                              x {group.item.qty_ordered}
                            </p>
                            <p>{group.item.product_code}</p>
                            <p className="text-xs text-gray-500">
                              Weight: {group.item.weight} lbs
                            </p>
                            {["16", "17"].includes(
                              group.item.product_code.substring(0, 2)
                            ) && (
                              <p
                                onClick={() =>
                                  fetchTrackMatches(group.item.product_code)
                                }
                                className="text-[var(--primary-color)] hover:cursor-pointer hover:text-[var(--secondary-color)]"
                              >
                                What does this fit?
                              </p>
                            )}
                          </div>

                          <p className="flex-none font-medium text-gray-900">
                            {group.item.price
                              ? `$${group.item.price?.toFixed(2)}`
                              : "-"}
                          </p>
                        </div>

                        {group.subitems?.length > 0 && (
                          <ul className="pl-10 space-y-4">
                            {group.subitems.map((subitem, index) => (
                              <li
                                key={subitem.id || index}
                                className="flex space-x-6"
                              >
                                <div className="flex-auto space-y-1">
                                  <h3 className="text-gray-900">
                                    <i>** {subitem.description}</i>
                                  </h3>
                                </div>

                                <p className="flex-none font-medium text-gray-900">
                                  {subitem.price
                                    ? `$${subitem.price?.toFixed(2)}`
                                    : ""}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>

            <div className="lg:col-start-2">
              <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Quote Details
              </p>

              <div className="lg:flex md:flex mt-8">
                <div>
                  <dt className="font-medium text-gray-900">
                    Quote Information
                  </dt>
                  <dd className="mt-2">
                    <div className="space-y-1">
                      <p className="font-medium">
                        Quote #{loadedQuote.quote_num}
                      </p>
                      {loadedQuote.customer_po && (
                        <p className="text-sm text-gray-600">
                          PO: {loadedQuote.customer_po}
                        </p>
                      )}
                      {loadedQuote.weight && (
                        <p className="text-sm text-gray-600">
                          Total Weight: {loadedQuote.weight} lbs
                        </p>
                      )}
                    </div>
                  </dd>
                </div>

                {loadedQuote.billing_address?.street?.trim() && (
                  <div className="mt-8 md:mt-0 lg:ml-32 md:ml-32">
                    <dt className="font-medium text-gray-900">
                      Billing Address
                    </dt>
                    <dd className="mt-2">
                      <address className="not-italic">
                        {loadedQuote.billing_address.billing_name?.trim() && (
                          <span className="block">
                            {loadedQuote.billing_address.billing_name}
                          </span>
                        )}
                        <span className="block">
                          {loadedQuote.billing_address.street}
                        </span>
                        {loadedQuote.billing_address.route?.trim() && (
                          <span className="block">
                            {loadedQuote.billing_address.route}
                          </span>
                        )}
                        {loadedQuote.billing_address.locale?.trim() && (
                          <span className="block">
                            {loadedQuote.billing_address.locale}
                          </span>
                        )}
                      </address>
                    </dd>
                  </div>
                )}
              </div>

              {loadedQuote.third_party_billing?.name && (
                <div className="mt-6">
                  <dt className="font-medium text-gray-900">
                    Freight Billing Address
                  </dt>
                  <dd className="mt-2">
                    <address className="not-italic">
                      <span className="block">
                        {loadedQuote.third_party_billing.name}
                      </span>
                      <span className="block">
                        {loadedQuote.third_party_billing.addr1}
                      </span>
                      <span className="block">
                        {loadedQuote.third_party_billing.addr2}
                      </span>
                      <span className="block">
                        {loadedQuote.third_party_billing.city +
                          ", " +
                          loadedQuote.third_party_billing.state +
                          " " +
                          loadedQuote.third_party_billing.zip_code}
                      </span>
                    </address>
                  </dd>
                </div>
              )}

              {loadedQuote.freight?.comments?.length > 0 && (
                <div className="mt-6">
                  <dt className="font-medium text-gray-900">Comments</dt>
                  <dd className="mt-2 space-y-1 text-sm text-gray-700">
                    {loadedQuote.freight.comments.map((comment, index) => (
                      <p key={index}>{comment}</p>
                    ))}
                  </dd>
                </div>
              )}

              <dl className="space-y-3 mt-6 border-t border-gray-200 pt-4 text-sm font-medium text-gray-500">
                <div className="flex justify-between">
                  <dt>Shipping and Handling</dt>
                  <dd className="text-gray-900">
                    ${loadedQuote.shipping_charge?.toFixed(2)}
                  </dd>
                </div>

                <div className="flex justify-between">
                  <dt>Tariff</dt>
                  <dd className="text-gray-900">
                    ${loadedQuote.tariff_amount?.toFixed(2)}
                  </dd>
                </div>

                <div className="flex justify-between">
                  <dt>Surcharges</dt>
                  <dd className="text-gray-900">
                    ${loadedQuote.surcharge?.toFixed(2)}
                  </dd>
                </div>

                <div className="flex justify-between">
                  <dt>Taxes</dt>
                  <dd className="text-gray-900">
                    ${loadedQuote.tax?.toFixed(2)}
                  </dd>
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 pt-6 text-gray-900">
                  <dt className="text-base font-medium">Total</dt>
                  <dd className="text-base font-bold">
                    ${loadedQuote.total?.toFixed(2)}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 border-t border-gray-200 py-6 text-right space-x-4">
                <Button
                  textContent="Send Quote"
                  type="positive"
                  onClick={() => setShowEmailPopup(true)}
                />

                <Link
                  to="/view-history"
                  className="text-sm font-medium text-[var(--primary-color)] hover:text-[var(--secondary-color)]"
                >
                  Back to History
                  <span aria-hidden="true"> &rarr;</span>
                </Link>
              </div>
            </div>

            <EmailPopup
              HeaderText="Quote"
              BodyText="Please enter any email addresses you would like to send the quote to."
              cancelFunction={() => setShowEmailPopup(false)}
              dropDownEmails={loadedQuote?.contact?.emails || []}
              SubmitFunction={handleSendEmail}
              show={showEmailPopup}
            />
          </div>
        </main>
      )}
    </>
  );
}
